import { Injectable } from "@angular/core";
import { ApiUtilsService } from "src/app/api/services/api-utils.service";
import { StorageUtilsService } from "src/app/storage/services/storage-utils.service";

@Injectable({
  providedIn: 'root'
})
export class ListErrorHandlerService {
  static readonly ACTIONS_MAP = {
    get: 'loaded',
    load: 'loaded',
    add: 'added',
    update: 'updated',
    delete: 'deleted',
    publish: 'published',
    restore: 'restored',
    move: 'moved',
    rename: 'renamed',
    upload: 'uploaded',
    restart: 'restarted',
    reboot: 'rebooted'
  } as const;
  constructor(private apiUtils: ApiUtilsService) {}

  processErrorCode(
    itemName: string,
    action: ListAction,
    rawError: any,
    errorCodes?: string[]
  ) {
    const messagePrefix = this.getPrefix(itemName, action, errorCodes);
    const tryAgain = messagePrefix + 'Please try again.';
    const tryAgainReload = messagePrefix + 'Please reload this page and try again.';
    const permissionRequired = 'You don’t have permission to do this. Please contact your system administrator.';
    const checkConnection = messagePrefix + 'Please check your connection, or proxy and firewall settings, and try again.';
    const error = this.apiUtils.getError(rawError);
    let errorString = error?.message ? (messagePrefix + error.message) : tryAgain;

    // Attempt to internationalize Storage error
    if (error) {
      const message = (action && StorageUtilsService.STORAGE_CLIENT_ERROR[action]) ?
        StorageUtilsService.STORAGE_CLIENT_ERROR[action][error.message] :
        StorageUtilsService.STORAGE_CLIENT_ERROR[error.message];
      if (message) {
        errorString = message;
      }
    }

    if (!error) {
      return errorString;
    } else if (error.status === 400) {
      if (errorString.indexOf('is not editable') >= 0) {
        return errorString;
      } else if (errorString.indexOf('is required') >= 0) {
        return errorString;
      } else {
        return errorString;
      }
    } else if (error.status === 401) {
      return tryAgainReload;
    } else if (error.status === 403) {
        if (errorString.indexOf('User is not allowed access') >= 0) {
          return permissionRequired;
        } else if (errorString.indexOf('User does not have the necessary rights') >= 0) {
          return permissionRequired;
        } else if (errorString.indexOf('Premium Template requires Purchase') >= 0) {
          return permissionRequired;
        } else if (errorString.indexOf('Storage requires active subscription') >= 0) {
          return permissionRequired;
        } else if (errorString.indexOf('Not allowed access') >= 0) {
          return permissionRequired;
        } else {
          return errorString;
        }
    } else if (error.status === 404) {
      return tryAgain;
    } else if (error.status === 409) {
      if (itemName === 'Presentations' && action === 'delete' && errorCodes && errorCodes.length > 1) {
        const otherErrors = errorCodes.filter(code => code !== '409');
        return messagePrefix + (otherErrors.length ? 'One or more of these ' : 'These ') +
          'presentations are embedded in other presentations. To delete them, ' +
          'they must be removed from the presentations they are being used in.';
      }
      return errorString;
    } else if (error.status === 500 || error.status === 503) {
      return tryAgain;
    } else if (error.status === -1 || error.code === -1 || error.code === 0) {
      return checkConnection;
    } else {
      return errorString;
    }
  }

  private getPrefix(itemName: string, action: ListAction, errorCodes?: string[]) {
    let messagePrefix ='Something went wrong. ';
    const actionName = ListErrorHandlerService.ACTIONS_MAP[action];

    if (itemName && actionName) {
      let itemNameLower = itemName.toLowerCase();
      if (errorCodes) {
        if (errorCodes.length === 1) {
          itemNameLower = itemNameLower.replace(/s$/, '');
        }
        messagePrefix = errorCodes.length + ' ' + itemNameLower + ' could not be ' + actionName + '. ';
      } else {
        messagePrefix = 'The ' + itemNameLower + ' could not be ' + actionName + '. ';
      }
    }

    return messagePrefix;
  }
}

export type ListAction = keyof typeof ListErrorHandlerService.ACTIONS_MAP;
